import type { ElementPosition } from './schema.types';

/**
 * Shared freeform geometry helpers. Every column in the editor is now a
 * freeform canvas (1-col / 2-col / 3-col rows just change how many canvases sit
 * side by side), so elements are positioned by `position.{x,y,w,h,rotation}`.
 */

export const DEFAULT_FREEFORM_POSITION = { x: 48, y: 48, w: 420, h: 220 };

/**
 * Deterministic fallback rect for an element that has no explicit `position`
 * yet (e.g. just dropped, or carried over from the old flow layout). Both the
 * editor canvas and the teach stage use this so an un-positioned element renders
 * in the SAME spot in both places (WYSIWYG). Elements cascade down the column
 * so multiple un-positioned blocks don't stack on top of each other.
 */
export function freeformFallbackRect(stackIndex = 0): Required<Pick<ElementPosition, 'x' | 'y' | 'w' | 'h'>> {
  return { x: 32, y: 24 + stackIndex * 132, w: 320, h: 120 };
}

/** Effective rect for an element: its explicit position, else the stack fallback. */
export function effectiveFreeformRect(
  position: ElementPosition | undefined,
  stackIndex = 0,
): { x: number; y: number; w: number; h: number; rotation: number; zIndex: number } {
  const fb = freeformFallbackRect(stackIndex);
  return {
    x: position?.x ?? fb.x,
    y: position?.y ?? fb.y,
    w: position?.w ?? fb.w,
    h: position?.h ?? fb.h,
    rotation: position?.rotation ?? 0,
    zIndex: position?.zIndex ?? stackIndex + 1,
  };
}
